import { useCallback, useRef } from "react";

interface PreventableEvent {
  preventDefault: () => void;
}

/**
 * Throttles a form submit handler to prevent multiple submissions within a short period of time.
 * Automatically calls `event.preventDefault()` to avoid page refresh, even when the handler
 * is throttled and not executed.

 * @example
 * ```tsx
 * // Direct form onSubmit usage
 * const handleSubmit = useThrottledSubmit((event: FormEvent<HTMLFormElement>) => { ... });
 * return <form onSubmit={handleSubmit}>...</form>;
 *
 * // With react-hook-form
 * const handleSubmit = useThrottledSubmit(form.handleSubmit(async (data) => { ... }));
 * return <form onSubmit={handleSubmit}>...</form>;
 * ```
 */
export const useThrottledSubmit = <TEvent extends PreventableEvent>(
  fn: (event?: TEvent) => void
) => {
  const isPendingRef = useRef(false);
  const timeoutRef = useRef<number>();

  const execute = useCallback(
    (event?: TEvent) => {
      // Always prevent default to avoid page refresh, even when throttled
      event?.preventDefault();

      if (isPendingRef.current) {
        return;
      }

      fn(event);

      isPendingRef.current = true;

      timeoutRef.current = window.setTimeout(() => {
        isPendingRef.current = false;
        timeoutRef.current = undefined;
      }, 1_000);
    },
    [fn]
  );

  return execute;
};
